/**
 * Error Handling Utilities
 * 
 * This module provides error classification, handling, and logging utilities
 * for the Podcast Amplify App.
 * 
 * Requirements: 13.1, 13.4
 */

/**
 * Error types that can occur in the application
 */
export const ErrorType = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  NOT_FOUND: 'NOT_FOUND',
  PARSE_ERROR: 'PARSE_ERROR',
  AUDIO_ERROR: 'AUDIO_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR'
} as const;

export type ErrorType = typeof ErrorType[keyof typeof ErrorType];

/**
 * Interface representing a structured application error
 */
export interface AppError {
  type: ErrorType;
  message: string;
  retryable: boolean;
  originalError?: Error;
}

/**
 * User-facing error messages for each error type
 */
export const errorMessages: Record<ErrorType, string> = {
  [ErrorType.NETWORK_ERROR]: 'Unable to load content. Please check your connection and try again.',
  [ErrorType.NOT_FOUND]: 'This episode could not be found.',
  [ErrorType.PARSE_ERROR]: 'Unable to process the content. Please try again later.',
  [ErrorType.AUDIO_ERROR]: 'Unable to play audio. Please try a different episode.',
  [ErrorType.UNKNOWN_ERROR]: 'Something went wrong. Please refresh the page.'
};

/**
 * Handles and classifies errors into structured AppError objects
 * 
 * @param error - The error to handle (can be any type)
 * @returns A structured AppError object with type, message, and retry information
 * 
 * @example
 * ```typescript
 * try {
 *   await fetchData();
 * } catch (error) {
 *   const appError = handleError(error);
 *   console.error(appError.message);
 * }
 * ```
 */
export function handleError(error: unknown): AppError {
  // Handle network/fetch errors
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return {
      type: ErrorType.NETWORK_ERROR,
      message: 'Unable to connect. Please check your internet connection.',
      retryable: true,
      originalError: error
    };
  }

  // Handle 404 Not Found errors
  if (error instanceof Response && error.status === 404) {
    return {
      type: ErrorType.NOT_FOUND,
      message: 'The requested content was not found.',
      retryable: false
    };
  }

  // Handle JSON parse errors
  if (error instanceof SyntaxError) {
    return {
      type: ErrorType.PARSE_ERROR,
      message: 'Unable to process the content. Please try again later.',
      retryable: true,
      originalError: error
    };
  }

  // Handle audio-related errors
  if (error instanceof Error && error.message.toLowerCase().includes('audio')) {
    return {
      type: ErrorType.AUDIO_ERROR,
      message: 'Unable to play audio. Please try a different episode.',
      retryable: true,
      originalError: error
    };
  }

  // Handle generic Error objects
  if (error instanceof Error) {
    return {
      type: ErrorType.UNKNOWN_ERROR,
      message: 'An unexpected error occurred. Please try again.',
      retryable: true,
      originalError: error
    };
  }

  // Handle unknown error types
  return {
    type: ErrorType.UNKNOWN_ERROR,
    message: 'Something went wrong. Please refresh the page.',
    retryable: true,
    originalError: error as Error
  };
}

/**
 * Logs errors to the browser console with structured context
 * 
 * @param error - The AppError to log
 * @param context - Optional additional context information
 * 
 * @example
 * ```typescript
 * const appError = handleError(error);
 * logError(appError, { episodeId: 'ep001', action: 'fetchEpisodeData' });
 * ```
 */
export function logError(error: AppError, context?: Record<string, any>): void {
  console.error('[App Error]', {
    type: error.type,
    message: error.message,
    retryable: error.retryable,
    timestamp: new Date().toISOString(),
    context,
    userAgent: navigator.userAgent,
    originalError: error.originalError
  });

  // Optional: Send to monitoring service
  // This can be extended in the future to send errors to a monitoring service
  // sendToMonitoring(error, context);
}

/**
 * Gets a user-friendly error message for a given error type
 * 
 * @param errorType - The type of error
 * @returns A user-friendly error message
 * 
 * @example
 * ```typescript
 * const message = getErrorMessage(ErrorType.NETWORK_ERROR);
 * // Returns: "Unable to load content. Please check your connection and try again."
 * ```
 */
export function getErrorMessage(errorType: ErrorType): string {
  return errorMessages[errorType];
}
